import { API } from "aws-amplify";

export type DeletedFilter = "all" | "exclude_deleted" | "deleted_only";

export type ProgramAssessmentListItem = {
  assessmentEventId: string;
  activityEventId: string;
  programId: string;
  activity: { id: string; title: string | null } | null;
  activityEventDate: string;
  completionDate: string | null;
  subjectUserId: string | null;
  raterUserId: string | null;
  subjectFullName: string | null;
  raterFullName: string | null;
  deletedAt: string | null;
  deletedByUserId: string | null;
  deletedByFullName: string | null;
  deletedReason: string | null;
  deletedNote: string | null;
};

const UUID_LIKE_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Show removal-audit rows when the assessment is deleted. */
export function programAssessmentHasDeletionAuditMetadata(row: {
  deletedAt: string | null;
}): boolean {
  return Boolean(row.deletedAt);
}

export function formatProgramAssessmentRemovedBy(
  deletedByFullName: string | null | undefined,
  deletedByUserId: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const name = typeof deletedByFullName === "string" ? deletedByFullName.trim() : "";
  if (name) {
    if (UUID_LIKE_ID.test(name)) {
      return t("User ID: {{id}}", { id: name }) as string;
    }
    return name;
  }
  const raw = typeof deletedByUserId === "string" ? deletedByUserId.trim() : "";
  if (!raw) {
    return "—";
  }
  if (UUID_LIKE_ID.test(raw)) {
    return t("User ID: {{id}}", { id: raw }) as string;
  }
  return raw;
}

export function programAssessmentDeletionReasonForDisplay(
  deletedReason: string | null | undefined,
  _deletedNote: string | null | undefined
): string {
  const reason = typeof deletedReason === "string" ? deletedReason.trim() : "";
  return reason || "—";
}

export function programAssessmentDeletionNoteForDisplay(
  _deletedReason: string | null | undefined,
  deletedNote: string | null | undefined
): string {
  const note = typeof deletedNote === "string" ? deletedNote.trim() : "";
  return note || "—";
}

export type ProgramAssessmentsListResponse = {
  success: boolean;
  items: ProgramAssessmentListItem[];
  nextCursor: string | null;
  hasMore: boolean;
  /**
   * Present on the first page of a search only (omitted on “load more” to save a DB round-trip).
   * When totalCountCapped is true, at least totalCount rows match; the true total was not fully counted.
   */
  totalCount?: number;
  totalCountCapped?: boolean;
};

export type ProgramAssessmentsListRequest = {
  programId: string;
  activityEventDateFrom: string;
  activityEventDateTo: string;
  traineeId?: string;
  attendingId?: string;
  activityId?: string;
  completionDateFrom?: string;
  completionDateTo?: string;
  deletedFilter?: DeletedFilter;
  limit?: number;
  cursor?: string | null;
  /** When true, first page request includes capped total count (slower). Default false for faster searches. */
  includeTotalCount?: boolean;
};

const PATH = "program/assessments/list";
const PATH_DETAIL = "program/assessments/detail";
const PATH_DELETE = "program/assessments/delete";

export const ASSESSMENT_DELETION_REASONS = [
  "Wrong Trainee",
  "Wrong Activity",
  "Wrong Date",
  "Duplicate Submission",
  "Other"
] as const;

export type AssessmentDeletionReason = (typeof ASSESSMENT_DELETION_REASONS)[number];

export type ProgramAssessmentAnswer = {
  answerEventId: string;
  questionId: string | null;
  questionTitle: string | null;
  inputTypeName: string | null;
  value: string | null;
};

export type ProgramAssessmentDetailHeader = {
  assessmentEventId: string;
  programId: string | null;
  activityEventId: string | null;
  activityId: string | null;
  activityTitle: string | null;
  activityEventDate: string | null;
  completionDate: string | null;
  subjectUserId: string | null;
  subjectFullName: string | null;
  raterUserId: string | null;
  raterFullName: string | null;
  deletedAt: string | null;
  deletedReason: string | null;
  deletedNote: string | null;
  deletedByUserId: string | null;
  deletedByFullName: string | null;
};

export type ProgramAssessmentDetailResponse = {
  success: boolean;
  primary: ProgramAssessmentDetailHeader;
  primaryAnswers: ProgramAssessmentAnswer[];
  selfAssessment: ProgramAssessmentDetailHeader | null;
  selfAnswers: ProgramAssessmentAnswer[];
};

export type ProgramAssessmentDeleteResponse = {
  success: boolean;
  assessmentEventId: string;
  deletedAssessmentEventIds: string[];
  deletedAt: string;
};

export async function fetchProgramAssessmentsPage(
  body: ProgramAssessmentsListRequest
): Promise<ProgramAssessmentsListResponse> {
  return (await API.post("simpl-api", PATH, {
    body
  })) as ProgramAssessmentsListResponse;
}

export async function fetchProgramAssessmentsPageSafe(
  body: ProgramAssessmentsListRequest
): Promise<{ ok: true; data: ProgramAssessmentsListResponse } | { ok: false; message: string }> {
  try {
    const data = await fetchProgramAssessmentsPage(body);
    return { ok: true, data };
  } catch (err: unknown) {
    const anyErr = err as {
      response?: { data?: { error?: string; message?: string } };
      message?: string;
    };
    const message =
      anyErr.response?.data?.error ||
      anyErr.response?.data?.message ||
      anyErr.message ||
      "Unknown error";
    return { ok: false, message };
  }
}

export async function fetchProgramAssessmentDetail(
  assessmentEventId: string
): Promise<ProgramAssessmentDetailResponse> {
  return (await API.post("simpl-api", PATH_DETAIL, {
    body: { assessmentEventId }
  })) as ProgramAssessmentDetailResponse;
}

export async function fetchProgramAssessmentDetailSafe(assessmentEventId: string): Promise<
  | { ok: true; data: ProgramAssessmentDetailResponse }
  | { ok: false; message: string }
> {
  try {
    const data = await fetchProgramAssessmentDetail(assessmentEventId);
    return { ok: true, data };
  } catch (err: unknown) {
    const anyErr = err as {
      response?: { data?: { error?: string; message?: string } };
      message?: string;
    };
    const message =
      anyErr.response?.data?.error ||
      anyErr.response?.data?.message ||
      anyErr.message ||
      "Unknown error";
    return { ok: false, message };
  }
}

export async function deleteProgramAssessment(body: {
  assessmentEventId: string;
  reason: AssessmentDeletionReason;
  note?: string | null;
}): Promise<ProgramAssessmentDeleteResponse> {
  return (await API.post("simpl-api", PATH_DELETE, {
    body: {
      assessmentEventId: body.assessmentEventId,
      reason: body.reason,
      note: body.note ?? null
    }
  })) as ProgramAssessmentDeleteResponse;
}

export async function deleteProgramAssessmentSafe(body: {
  assessmentEventId: string;
  reason: AssessmentDeletionReason;
  note?: string | null;
}): Promise<
  | { ok: true; data: ProgramAssessmentDeleteResponse }
  | { ok: false; message: string; code?: string }
> {
  try {
    const data = await deleteProgramAssessment(body);
    return { ok: true, data };
  } catch (err: unknown) {
    const anyErr = err as {
      response?: { data?: { error?: string; message?: string; code?: string } };
      message?: string;
    };
    const message =
      anyErr.response?.data?.error ||
      anyErr.response?.data?.message ||
      anyErr.message ||
      "Unknown error";
    const code =
      typeof anyErr.response?.data?.code === "string"
        ? anyErr.response.data.code
        : undefined;
    return { ok: false, message, code };
  }
}
